import { $, $$, setAttributes } from '../../../shared/js/utils/dom';
import Shower from 'modules/shower';
import { uniqueId } from 'utils/helper';
import { Cookiebanner, cookiebannerLabels } from '@3pc/cookiebanner';
import { cookiebannerTemplateMinimal } from './cookiebanner-template';
import { initConsentOverlays } from '../consent-overlay/consent-overlay';

const consentOverlays = initConsentOverlays();

// tracking:
const isProduction = document.body.hasAttribute('data-production');
const siteId = document.body.getAttribute('data-tracking-id');
const matomoSiteId = isProduction ? siteId : 'QA.LINUX15.PROPERTY.ID';
const matomoUrl = isProduction
  ? 'https://stats.unesco.de/'
  : 'https://qa.linux15.3pc.de/matomo/';

// hiding the cookiebanner on certain pages (here: privacy policy and imprint):
const hideCookiebanner = document.body.hasAttribute('data-hide-cookiebanner');
const imprintLink =
  document.querySelector('[data-imprint-link]') &&
  JSON.parse(document.querySelector('[data-imprint-link]').dataset.imprintLink);
const privacyPolicyLink =
  document.querySelector('[data-privacy-policy-link]') &&
  JSON.parse(
    document.querySelector('[data-privacy-policy-link]').dataset
      .privacyPolicyLink
  );

const consentTypes = [
  {
    name: {
      de: 'Erforderliche Cookies',
      en: 'Necessary cookies',
    },
    text: {
      de: `<p>Der technische Aufbau der Website erfordert es, dass wir Techniken, insbesondere Cookies, nutzen. Ohne diese Techniken kann unsere Website nicht (vollständig korrekt) angezeigt werden oder die Support-Funktionen nicht ermöglicht werden. Dabei handelt es sich grundsätzlich um transiente Cookies, die nach Ende Ihres Website-Besuchs, spätestens bei Schließen Ihres Browsers, gelöscht werden. Diese Cookies können Sie nicht abwählen, wenn Sie unsere Website nutzen möchten.</p>`,
      en: `<p>Due to the technical structure of our website, we are required to use various analytical tools, such as cookies. Without such tools, our website would not be displayed (entirely) correctly and certain support functions wouldn’t work. These tools are basically temporary session cookies that are deleted after you leave our website, and at the latest, when you close your browser. If you wish to use our website, you cannot opt out of these cookies.</p>`,
    },
    type: 'required',
    showInInfoPanel: true,
  },
  {
    name: {
      de: 'Tracking-Cookies',
      en: 'Tracking cookies',
    },
    text: {
      de: `<p>Verschiedene Cookies oder weitere Techniken nutzen wir nur nach Ihrer Einwilligung. Die Funktionen werden nur im Falle Ihrer Zustimmung aktiviert und können insbesondere dazu dienen, dass wir die Besuche auf unserer Website analysieren und verbessern können, Ihnen die Bedienung über verschiedene Browser oder Endgeräte zu erleichtern, Sie bei einem Besuch wiederzuerkennen und die Effektivität von Anzeigen zu messen.</p>`,
      en: `<p>Other types of cookies and technologies are only used with your explicit consent. These cookies activate additional functions and allow us, in particular, to analyse how you engage with our website. This in turn enables us to improve your online experience, makes it easier for you to visit us using different browsers or terminal devices, recognises you when you visit us again and measures the effectiveness of advertisements.</p>`,
    },
    type: 'matomo',
    showInInfoPanel: true,
    trackingScripts: [
      {
        insertSelector: 'body',
        script: `
           var _paq = window._paq = window._paq || [];
          _paq.push(['trackPageView']);
          _paq.push(['enableLinkTracking']);
          (function() {
          var u="${matomoUrl}";
          _paq.push(['setTrackerUrl', u+'matomo.php']);
          _paq.push(['setSiteId', '${matomoSiteId}'])
          var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
          g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
          })();`,
      },
    ],
  },
  {
    name: {
      de: 'Video Cookies',
      en: 'Video',
    },
    text: {
      de: '<p>Mit unseren Videos möchten wir Sie über unsere Angebote informieren. Wir binden diese Videos von unseren eigenen YouTube-Kanälen im erweiterten Datenschutzmodus ein. YouTube ist ein Plattform-Dienst der Google Inc., 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA.</p><p>Sie können Ihre Cookie-Einstellungen jederzeit ändern und die Änderung durch erneutes Laden der Internetseite aktivieren.</p>',
      en: 'Text || Youtube content.',
    },
    type: 'youtube',
  },
  {
    name: {
      de: 'Eingebettete Inhalte',
      en: 'Iframes',
    },
    text: {
      de: '<p>Um Ihnen erweiterte Funktionen und Services auf unseren Websites anzubieten, betten wir auf unseren Internetseiten ausgewählte Inhalte von Fremdbetreibern bzw. Dienstleistern ein. Dies betrifft insbesondere einige Formulare.</p><p>Sie können Ihre Cookie-Einstellungen jederzeit ändern und die Änderung durch erneutes Laden der Internetseite aktivieren.</p>',
      en: 'Text || Iframes.',
    },
    type: 'iframe',
  },
];

cookiebannerLabels.mainPanelTitle = {
  de: 'Einwilligung in die Verwendung von Cookies und in die Verarbeitung personenbezogener Daten.',
  en: 'Declaration of consent on cookie usage and the processing of personal data',
};

cookiebannerLabels.mainPanelText = {
  de: `<p>Durch Ihre Einwilligung bestimmen Sie den Umfang der Funktionen auf unserer Website. Über die Kategorien „Nur notwendige Cookies zulassen“ und „Auch-Tracking-Cookies zulassen“ können Sie individuell entscheiden, ob Sie die von uns vorgesehenen Funktionalitäten aktivieren möchten. Mit dem Klick auf den Button „Auch-Tracking-Cookies zulassen“ geben Sie Ihr Einverständnis, dass wir – je nach Auswahl - Ihre Aktivitäten auf der Website analysieren, indem wir dazu Cookies einsetzten. Die Analyse erfolgt durch dafür spezifisch eingesetzte Dienstleister, die die Daten unter Umständen auch in den USA verarbeiten. Die Unternehmen haben sich dem Privacy Framework unterworfen. Detaillierte Informationen zu den von uns eingesetzten Analysetools finden Sie in der <a class="styled-link styled-link--internal-underline" href="${
    privacyPolicyLink ? privacyPolicyLink.url : '/'
  }"><span class="styled-link__text">Datenschutzerklärung</span></a>. Der Umfang Ihrer Einwilligung richtet sich nach Ihrer Auswahl der Kategorien des Funktionsumfangs.</p><p>Hinweis: Weitere Informationen zur Datenverarbeitung erhalten Sie in der <a class="styled-link styled-link--internal-underline" href="${
    privacyPolicyLink ? privacyPolicyLink.url : '/'
  }"><span class="styled-link__text">Datenschutzerklärung</span></a>. Sie können die Einwilligung jederzeit durch Anklicken des Buttons „Einwilligung widerrufen“ widerrufen, wenn Sie am Ende der Website den Cookie-Banner erneut öffnen. Der Widerruf hat keine Auswirkung auf die Zulässigkeit der bisherigen Datenverarbeitung.</p>`,
  en: `<p>With your consent, you can influence the scope of functions that are available to you on our website. By clicking the options ‘Only allow necessary cookies’ and/or ‘Also allow tracking cookies’, you decide whether to take advantage of only some or all the functions offered on our website. By clicking the button ‘Also allow tracking cookies’, you grant us permission to use cookies to analyse your activities while visiting the website. This information allows us to enhance the functionality of our website and improve user experience. The analysis is carried out by specifically contracted service providers who may also process the data on their servers in the USA. These companies are obliged to comply with the EU-US Privacy Framework. Please refer to our <a class="styled-link styled-link--internal-underline" href="${
    privacyPolicyLink ? privacyPolicyLink.url : '/'
  }"><span class="styled-link__text">Data Protection Policy</span></a> for detailed information on the website analysis tools we use. Depending on the consent options you choose, you determine the scope of functions we can offer you on our website.</p><p>Please note: For more details on how your personal data is processed, please refer to our <a class="styled-link styled-link--internal-underline" href="${
    privacyPolicyLink ? privacyPolicyLink.url : '/'
  }"><span class="styled-link__text">Data Protection Policy</span></a>. You may revoke your consent at any time by clicking the button ‘Revoke consent’ when you reopen the cookie banner at the end of the website. Withdrawing consent has no effect on the legality of the data processed prior to revocation.</p>`,
};

// init Cookiebanner
new Cookiebanner({
  nameSpace: 'duk',
  consentButtonTypes: 'required-tracking',
  cookiebannerTemplate: cookiebannerTemplateMinimal,
  showTemplateMinimalInfoPanel: true,
  labels: cookiebannerLabels,
  hideCookiebanner,
  privacyPolicyLink,
  imprintLink,
  consentTypes,
  onInit: () => {
    // e.g. init ConsentOverlay
  },
  onRender: banner => {
    const infoPanelAccordions = $$('.cookiebanner__info-panel li', banner);
    infoPanelAccordions.length &&
      infoPanelAccordions.forEach(accordion => {
        const randomID = uniqueId();
        const target = $('.cookiebanner__shower-panel', accordion);
        const handler = $('button', accordion);

        setAttributes(handler, {
          id: `acc-btn-${randomID}`,
          'aria-controls': `acc-tab-${randomID}`,
        });

        setAttributes(target, {
          id: `acc-tab-${randomID}`,
          'aria-labelledby': `acc-btn-${randomID}`,
        });

        new Shower({
          target,
          handler,
          easing: 'easeInOutQuart',
          duration: 400,
          initOpened: false,
          openClassName: 'is-active',
        }).init();
      });
  },
  onOptIn: function (consentType) {
    consentOverlays.forEach(overlay => {
      if (consentType.type === overlay.consentType) overlay.toggleAll();
    });
  },
  onOptOut: function (consentType) {
    consentOverlays.forEach(overlay => {
      if (consentType.type === overlay.consentType) overlay.toggleAll();
    });
  },
  onTrackingEnabled: consentType => {
    // e.g. init event tracking
  },
}).init();
